import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { getCategoryName } from '@/utils/news';

interface HeroCarouselProps {
  articles: NewsArticle[];
}

export default function HeroCarousel({ articles }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = articles.length;

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const go = (next: number) => setIndex(((next % total) + total) % total);
  const current = articles[index];

  return (
    <section className="relative overflow-hidden rounded-lg bg-primary-950">
      <div className="relative h-[380px] w-full md:h-[520px]">
        {articles.map((article, i) => (
          <img
            key={article.id}
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 爆料网焦点新闻`}
            className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10" />

        <div className="absolute inset-x-0 bottom-0 p-5 md:p-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                <i className="ri-fire-fill" />
                焦点爆料
              </span>
              <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-50">
                {getCategoryName(current.category)}
              </span>
            </div>
            <h2 className="mt-3 font-heading text-xl font-black leading-8 text-background-50 md:text-3xl md:leading-[2.75rem]">
              <Link to={`/detail/${current.id}`} className="transition-opacity hover:opacity-90">
                {current.title}
              </Link>
            </h2>
            <p className="mt-3 hidden max-w-xl text-sm leading-6 text-background-50/85 md:block">
              {current.summary}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
              >
                查看详情
                <i className="ri-arrow-right-line" />
              </Link>
              <span className="hidden text-xs text-background-50/70 sm:block">
                {current.source} · {current.publishTime}
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一条"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/35 md:left-5 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一条"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/35 md:right-5 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-4 right-5 hidden items-center gap-2 md:flex">
          {articles.map((article, i) => (
            <button
              key={article.id}
              type="button"
              aria-label={`切换到第${i + 1}条焦点新闻`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/60 hover:bg-background-50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}